import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { trendingAnime, latestUpdates, categories, regions, years } from '@/mocks/anime';

const allAnime = [...trendingAnime, ...latestUpdates];

const categoryMap: Record<string, string> = {
  rexue: '热血', lianai: '恋爱', qihuan: '奇幻', gaoxiao: '搞笑',
  zhanji: '战斗', xuanyi: '悬疑', richang: '日常', kehuan: '科幻',
  maoxian: '冒险', xiaoyuan: '校园',
  riben: '日本动漫', guoman: '国漫', oumei: '欧美动漫',
};

const yearMap: Record<string, string> = {
  '2026': '2026年', '2025': '2025年', '2024': '2024年',
  '2023': '2023年', zaonian: '早年经典',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'type' | 'region' | 'year'>('type');

  const currentCategoryName = categoryMap[slug || ''] || yearMap[slug || ''] || '全部';

  const filteredAnime = useMemo(() => {
    if (!slug) return allAnime;
    if (categoryMap[slug]) return allAnime.filter(a => a.category === slug);
    if (regionMap[slug]) return allAnime.filter(a => a.region === slug);
    if (yearMap[slug]) return allAnime.filter(a => a.year === slug);
    return allAnime;
  }, [slug]);

  const allCategorySlugs = categories.map(c => c.slug);
  const regionMapInternal: Record<string, string> = {};
  regions.forEach(r => { regionMapInternal[r.slug] = r.name; });

  const tabs = {
    type: { label: '按类型', items: categories },
    region: { label: '按地区', items: regions.map(r => ({ slug: r.slug, name: r.name, icon: 'ri-global-line', color: 'secondary' as const })) },
    year: { label: '按年份', items: years.map(y => ({ slug: y.slug, name: y.name, icon: 'ri-calendar-line', color: 'secondary' as const })) },
  };

  const currentTab = tabs[activeTab];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-3" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{currentCategoryName}动漫</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {currentCategoryName}动漫
              <span className="text-base font-body text-foreground-500 ml-3 font-normal">共 {filteredAnime.length} 部作品</span>
            </h1>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-2">
              {(['type', 'region', 'year'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {tabs[tab].label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-wrap gap-2 mb-8">
            {currentTab.items.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                className={`px-4 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${
                  slug === item.slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 border border-transparent hover:border-primary-200'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {filteredAnime.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredAnime.map((anime) => (
                <Link
                  key={anime.id}
                  to={`/article/${anime.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={anime.coverUrl}
                      alt={anime.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      title={`${anime.title} - 樱花动漫之家`}
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        {anime.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-background-50 text-xs">{anime.episode}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    {anime.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500">{tag}</span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">暂无该分类的动漫作品</p>
              <Link
                to="/"
                className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                返回首页浏览更多
              </Link>
            </div>
          )}
        </div>

        <script type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": `${currentCategoryName}动漫 - 樱花动漫之家`,
              "description": `樱花动漫之家的${currentCategoryName}动漫分类，提供海量${currentCategoryName}动漫资源在线观看。`,
              "url": `https://www.yinghuadongman.com/category/${slug}`,
              "isPartOf": { "@id": "https://www.yinghuadongman.com/" }
            })
          }}
        />
      </main>
      <Footer />
    </>
  );
}